import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useApp } from '../../context/AppContext.tsx';
import { compressImageFile, formatFileSize } from '../../utils/mediaOptimizer.ts';
import { parseGoogleMapsEmbedUrl } from '../../lib/contactUtils.ts';
import ConfirmModal from '../common/ConfirmModal.tsx';
import {
  FileText,
  Compass,
  Phone,
  MapPin,
  Mail,
  MessageCircle,
  Plus,
  Trash2,
  Edit3,
  Check,
  ExternalLink,
  Copy,
  Upload,
  Eye,
  Star,
  ShieldCheck,
  Award,
  HeartHandshake,
  Clock,
  Layers,
  HelpCircle,
  Info,
  CheckCircle2,
  Globe,
  Share2,
  Save,
  Link as LinkIcon,
  Sliders,
  Search,
} from 'lucide-react';
import { CustomStaticPage, StaticPageValueItem, PageTabTitlesConfig } from '../../types.ts';

type ActivePageTab = 'urls' | 'tab_titles' | 'about' | 'contact' | 'custom';

export default function PagesManager() {
  const { settings, updateSettingsOnServer, showToast, copyPageUrl, navigate } = useApp();
  const [activeTab, setActiveTab] = useState<ActivePageTab>('urls');

  // Browser Tab Titles Form State
  const [tabTitlesForm, setTabTitlesForm] = useState<PageTabTitlesConfig>({
    home: settings?.pageTabTitles?.home || '',
    blog: settings?.pageTabTitles?.blog || '',
    products: settings?.pageTabTitles?.products || '',
    portfolio: settings?.pageTabTitles?.portfolio || '',
    gallery: settings?.pageTabTitles?.gallery || '',
    mediaShowcase: settings?.pageTabTitles?.mediaShowcase || '',
    about: settings?.pageTabTitles?.about || '',
    contact: settings?.pageTabTitles?.contact || '',
    titleSuffix: settings?.pageTabTitles?.titleSuffix !== undefined ? settings.pageTabTitles.titleSuffix : ` | ${settings?.siteName || 'ALY LAW FIRM'}`,
  });
  const [isSavingTabTitles, setIsSavingTabTitles] = useState(false);
  const [tabTitlesSavedSuccess, setTabTitlesSavedSuccess] = useState(false);
  const [activeTabPreviewKey, setActiveTabPreviewKey] = useState<keyof PageTabTitlesConfig>('home');

  // URL & Slugs Form State
  const [slugsForm, setSlugsForm] = useState({
    products: settings?.pageSlugs?.products || settings?.pageSlugs?.productsSlug || 'produk',
    articles: settings?.pageSlugs?.articles || settings?.pageSlugs?.blogSlug || 'blog',
    portfolio: settings?.pageSlugs?.portfolio || settings?.pageSlugs?.portfolioSlug || 'portofolio',
    gallery: settings?.pageSlugs?.gallery || settings?.pageSlugs?.gallerySlug || 'foto-video',
    mediaShowcase: settings?.pageSlugs?.mediaShowcase || settings?.pageSlugs?.mediaShowcaseSlug || 'media-showcase',
  });
  const [isSavingSlugs, setIsSavingSlugs] = useState(false);
  const [slugsSavedSuccess, setSlugsSavedSuccess] = useState(false);
  const [urlSearchQuery, setUrlSearchQuery] = useState('');

  // Synchronize tabTitles when settings change
  React.useEffect(() => {
    if (settings?.pageTabTitles) {
      setTabTitlesForm({
        home: settings.pageTabTitles.home || '',
        blog: settings.pageTabTitles.blog || '',
        products: settings.pageTabTitles.products || '',
        portfolio: settings.pageTabTitles.portfolio || '',
        gallery: settings.pageTabTitles.gallery || '',
        mediaShowcase: settings.pageTabTitles.mediaShowcase || '',
        about: settings.pageTabTitles.about || '',
        contact: settings.pageTabTitles.contact || '',
        titleSuffix: settings.pageTabTitles.titleSuffix !== undefined ? settings.pageTabTitles.titleSuffix : ` | ${settings?.siteName || 'ALY LAW FIRM'}`,
      });
    }
  }, [settings?.pageTabTitles, settings?.siteName]);

  const handleSaveTabTitles = async () => {
    setIsSavingTabTitles(true);
    setTabTitlesSavedSuccess(false);
    try {
      await updateSettingsOnServer({
        pageTabTitles: tabTitlesForm,
      });
      setTabTitlesSavedSuccess(true);
      showToast('Kustomisasi judul tab browser per halaman berhasil disimpan!', 'success');
      setTimeout(() => setTabTitlesSavedSuccess(false), 4000);
    } catch (err: any) {
      showToast(err.message || 'Gagal menyimpan judul tab browser', 'error');
    } finally {
      setIsSavingTabTitles(false);
    }
  };

  // Synchronize slugs when settings change
  React.useEffect(() => {
    if (settings?.pageSlugs) {
      setSlugsForm({
        products: settings.pageSlugs.products || settings.pageSlugs.productsSlug || 'produk',
        articles: settings.pageSlugs.articles || settings.pageSlugs.blogSlug || 'blog',
        portfolio: settings.pageSlugs.portfolio || settings.pageSlugs.portfolioSlug || 'portofolio',
        gallery: settings.pageSlugs.gallery || settings.pageSlugs.gallerySlug || 'foto-video',
        mediaShowcase: settings.pageSlugs.mediaShowcase || settings.pageSlugs.mediaShowcaseSlug || 'media-showcase',
      });
    }
  }, [settings?.pageSlugs]);

  const handleSaveAllSlugs = async () => {
    setIsSavingSlugs(true);
    setSlugsSavedSuccess(false);
    try {
      const cleanSlug = (val: string, fallback: string) =>
        val.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || fallback;

      const cleanProducts = cleanSlug(slugsForm.products, 'produk');
      const cleanArticles = cleanSlug(slugsForm.articles, 'blog');
      const cleanPortfolio = cleanSlug(slugsForm.portfolio, 'portofolio');
      const cleanGallery = cleanSlug(slugsForm.gallery, 'foto-video');
      const cleanMedia = cleanSlug(slugsForm.mediaShowcase, 'media-showcase');

      const updatedSlugs = {
        ...(settings?.pageSlugs || {}),
        products: cleanProducts,
        productsSlug: cleanProducts,
        articles: cleanArticles,
        articlesSlug: cleanArticles,
        blogSlug: cleanArticles,
        portfolio: cleanPortfolio,
        portfolioSlug: cleanPortfolio,
        gallery: cleanGallery,
        gallerySlug: cleanGallery,
        mediaShowcase: cleanMedia,
        mediaShowcaseSlug: cleanMedia,
      };

      setSlugsForm({
        products: cleanProducts,
        articles: cleanArticles,
        portfolio: cleanPortfolio,
        gallery: cleanGallery,
        mediaShowcase: cleanMedia,
      });

      const ok = await updateSettingsOnServer({ pageSlugs: updatedSlugs as any });
      if (ok) {
        setSlugsSavedSuccess(true);
        setTimeout(() => setSlugsSavedSuccess(false), 4000);
        showToast('Semua Pengaturan Slug URL Halaman berhasil disimpan & aktif secara instan!', 'success', 'URL Tersimpan');
      }
    } catch (err) {
      console.error(err);
      showToast('Gagal menyimpan pengaturan slug URL.', 'error');
    } finally {
      setIsSavingSlugs(false);
    }
  };

  // About Us State
  const [aboutData, setAboutData] = useState({
    title: settings?.staticPages?.aboutUs?.title || 'Tentang Kami',
    subtitle:
      settings?.staticPages?.aboutUs?.subtitle ||
      'Membangun keunggulan pendampingan hukum, advokasi berintegritas, dan perlindungan hukum bagi klien individu maupun korporasi',
    badge: settings?.staticPages?.aboutUs?.badge || 'Profil Kantor Advokat & Nilai Inti',
    content:
      settings?.staticPages?.aboutUs?.content ||
      `## Selamat Datang di ALY LAW FIRM\n\nKami adalah kantor advokat dan konsultan hukum terpercaya yang berfokus pada keunggulan layanan pendampingan hukum, integritas, dan perlindungan hak-hak hukum klien secara komprehensif.`,
    coverImage:
      settings?.staticPages?.aboutUs?.coverImage ||
      'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=1200&q=80',
    values: settings?.staticPages?.aboutUs?.values || [
      {
        id: 'val-1',
        icon: 'Compass',
        title: 'Integritas & Keahlian Legal',
        description: 'Mendampingi perkara perdata, pidana, dan korporasi dengan standar etika profesi advokat yang tinggi.',
      },
      {
        id: 'val-2',
        icon: 'HeartHandshake',
        title: 'Perlindungan Hak Klien',
        description: 'Menjaga kerahasiaan dan memberikan strategi hukum terbaik serta terukur di setiap jenjang peradilan.',
      },
      {
        id: 'val-3',
        icon: 'Award',
        title: 'Pengalaman & Komitmen',
        description: 'Didukung oleh Advokat & Konsultan Hukum berpengalaman dalam penanganan perkara kompleks.',
      },
    ],
    metaTitle: settings?.staticPages?.aboutUs?.metaTitle || '',
    metaDescription: settings?.staticPages?.aboutUs?.metaDescription || '',
  });

  // Contact Us State
  const [contactData, setContactData] = useState({
    title: settings?.staticPages?.contactUs?.title || 'Hubungi Kami',
    subtitle:
      settings?.staticPages?.contactUs?.subtitle ||
      'Konsultasikan kebutuhan hukum, advokasi, dan legalitas bisnis langsung dengan tim Advokat & Konsultan Hukum ALY LAW FIRM',
    badge: settings?.staticPages?.contactUs?.badge || 'Konsultasi & Penanganan Perkara 24/7',
    content:
      settings?.staticPages?.contactUs?.content ||
      `### Layanan Konsultasi & Advokasi Hukum 24/7\n\nMemiliki permasalahan hukum, kebutuhan pendampingan advokasi, atau konsultasi legalitas bisnis? Tim Advokat ALY LAW FIRM siap membantu solusi hukum presisi dan terpercaya bagi Anda.`,
    mapEmbedUrl:
      settings?.staticPages?.contactUs?.mapEmbedUrl ||
      'https://maps.google.com/maps?q=Jakarta,Indonesia&t=&z=13&ie=UTF8&iwloc=&output=embed',
    metaTitle: settings?.staticPages?.contactUs?.metaTitle || '',
    metaDescription: settings?.staticPages?.contactUs?.metaDescription || '',
  });

  // Custom Pages State
  const [customPages, setCustomPages] = useState<CustomStaticPage[]>(
    settings?.staticPages?.customPages || []
  );
  const [editingCustomPage, setEditingCustomPage] = useState<CustomStaticPage | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  // New custom page form state
  const [newPageForm, setNewPageForm] = useState<CustomStaticPage>({
    id: '',
    slug: '',
    title: '',
    subtitle: '',
    content: 'Tulis konten halaman di sini menggunakan format Markdown...',
    coverImage: '',
    metaTitle: '',
    metaDescription: '',
    status: 'published',
    updatedAt: new Date().toISOString(),
  });

  const [uploadingImage, setUploadingImage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activePreview, setActivePreview] = useState(false);

  // Handle Cover Image Upload with compression
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, target: 'about' | 'custom') => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      showToast('Mengompres dan mengunggah foto sampul...', 'info');
      const result = await compressImageFile(file, 1600, 1000, 0.8);

      if (target === 'about') {
        setAboutData((prev) => ({ ...prev, coverImage: result.dataUrl }));
      } else if (editingCustomPage) {
        setEditingCustomPage((prev) => (prev ? { ...prev, coverImage: result.dataUrl } : null));
      } else {
        setNewPageForm((prev) => ({ ...prev, coverImage: result.dataUrl }));
      }

      showToast(`Foto berhasil dioptimasi (${formatFileSize(result.compressedSize)})!`, 'success');
    } catch (err) {
      console.error('Error uploading image:', err);
      showToast('Gagal memproses gambar.', 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  // Add a value/pillar item in About Us
  const addValueItem = () => {
    const newItem: StaticPageValueItem = {
      id: `val-${Date.now()}`,
      icon: 'Star',
      title: 'Poin Nilai Baru',
      description: 'Deskripsi keunggulan atau komitmen perusahaan...',
    };
    setAboutData((prev) => ({ ...prev, values: [...prev.values, newItem] }));
  };

  const removeValueItem = (id: string) => {
    setAboutData((prev) => ({ ...prev, values: prev.values.filter((v) => v.id !== id) }));
  };

  const updateValueItem = (id: string, field: keyof StaticPageValueItem, value: string) => {
    setAboutData((prev) => ({
      ...prev,
      values: prev.values.map((v) => (v.id === id ? { ...v, [field]: value } : v)),
    }));
  };

  // Save About Us
  const handleSaveAbout = async () => {
    setIsSaving(true);
    try {
      const updatedStaticPages = {
        ...(settings?.staticPages || {}),
        aboutUs: aboutData,
        contactUs: contactData,
        customPages: customPages,
      };

      const ok = await updateSettingsOnServer({ staticPages: updatedStaticPages as any });
      if (ok) {
        showToast('Halaman "Tentang Kami" berhasil disimpan!', 'success', 'Tersimpan');
      }
    } catch (err) {
      console.error(err);
      showToast('Gagal menyimpan halaman Tentang Kami.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Save Contact Us
  const handleSaveContact = async () => {
    setIsSaving(true);
    try {
      const updatedStaticPages = {
        ...(settings?.staticPages || {}),
        aboutUs: aboutData,
        contactUs: contactData,
        customPages: customPages,
      };

      const ok = await updateSettingsOnServer({ staticPages: updatedStaticPages as any });
      if (ok) {
        showToast('Halaman "Hubungi Kami" & Peta berhasil disimpan!', 'success', 'Tersimpan');
      }
    } catch (err) {
      console.error(err);
      showToast('Gagal menyimpan halaman Hubungi Kami.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Save Custom Page (Create or Update)
  const handleSaveCustomPage = async (pageToSave: CustomStaticPage) => {
    if (!pageToSave.title.trim()) {
      showToast('Mohon masukkan judul halaman.', 'error');
      return;
    }

    let slug = pageToSave.slug.trim().toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    if (!slug) {
      slug = pageToSave.title.trim().toLowerCase()
        .replace(/[^a-z0-9-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
    }

    const finalPage: CustomStaticPage = {
      ...pageToSave,
      id: pageToSave.id || `cp-${Date.now()}`,
      slug,
      updatedAt: new Date().toISOString(),
    };

    let updatedList: CustomStaticPage[];
    const exists = customPages.some((p) => p.id === finalPage.id);
    if (exists) {
      updatedList = customPages.map((p) => (p.id === finalPage.id ? finalPage : p));
    } else {
      updatedList = [...customPages, finalPage];
    }

    setCustomPages(updatedList);
    setIsSaving(true);

    try {
      const updatedStaticPages = {
        ...(settings?.staticPages || {}),
        aboutUs: aboutData,
        contactUs: contactData,
        customPages: updatedList,
      };

      const ok = await updateSettingsOnServer({ staticPages: updatedStaticPages as any });
      if (ok) {
        showToast(`Halaman "${finalPage.title}" berhasil disimpan!`, 'success');
        setEditingCustomPage(null);
        setIsCreatingNew(false);
      }
    } catch (err) {
      console.error(err);
      showToast('Gagal menyimpan halaman.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const [pageToDelete, setPageToDelete] = useState<CustomStaticPage | null>(null);

  const executeDeleteCustomPage = async () => {
    if (!pageToDelete) return;
    const { id, title } = pageToDelete;

    const updatedList = customPages.filter((p) => p.id !== id);
    setCustomPages(updatedList);
    try {
      const updatedStaticPages = {
        ...(settings?.staticPages || {}),
        aboutUs: aboutData,
        contactUs: contactData,
        customPages: updatedList,
      };
      await updateSettingsOnServer({ staticPages: updatedStaticPages as any });
      showToast(`Halaman "${title}" telah dihapus.`, 'info');
      setPageToDelete(null);
    } catch (err) {
      console.error(err);
      showToast('Gagal menghapus halaman.', 'error');
      setPageToDelete(null);
    }
  };

  return (
    <div id="pages-manager-container" className="space-y-6">
      {/* Header Banner */}
      {/* Header Banner - Cleanly stacked so tabs and titles never squeeze or overflow */}
      <div className="bg-white p-5 sm:p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs font-bold mb-2 border border-emerald-200">
            <FileText className="w-3.5 h-3.5 text-emerald-600" />
            <span>Kelola Halaman & Informasi Statis</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug">
            Pengaturan Halaman Tentang Kami, Kontak, Halaman Kustom & URL Slug
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1 max-w-3xl">
            Sesuaikan konten Tentang Kami, Kontak & Peta Google Maps, Halaman Kustom, serta pusat pengelolaan slug URL permanen seluruh website.
          </p>
        </div>

        {/* Tab Switcher - Responsive Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 p-1.5 bg-slate-100 rounded-xl border border-slate-200">
          <button
            type="button"
            onClick={() => setActiveTab('urls')}
            className={`px-3 py-2.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
              activeTab === 'urls'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-700 hover:text-slate-900 hover:bg-white/80 bg-white sm:bg-transparent'
            }`}
          >
            <Globe className="w-4 h-4 shrink-0" />
            <span className="truncate">URL & Slug</span>
            <span className={`px-1.5 py-0.5 text-[9px] rounded font-black uppercase shrink-0 ${
              activeTab === 'urls' ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-800'
            }`}>
              Semua
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('tab_titles')}
            className={`px-3 py-2.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
              activeTab === 'tab_titles'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-700 hover:text-slate-900 hover:bg-white/80 bg-white sm:bg-transparent'
            }`}
          >
            <Sliders className="w-4 h-4 shrink-0" />
            <span className="truncate">Judul Tab Browser</span>
            <span className={`px-1.5 py-0.5 text-[9px] rounded font-black uppercase shrink-0 ${
              activeTab === 'tab_titles' ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-800'
            }`}>
              Tab
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('about')}
            className={`px-3 py-2.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
              activeTab === 'about'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-700 hover:text-slate-900 hover:bg-white/80 bg-white sm:bg-transparent'
            }`}
          >
            <Compass className="w-4 h-4 shrink-0" />
            <span className="truncate">Tentang Kami (/about)</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('contact')}
            className={`px-3 py-2.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
              activeTab === 'contact'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-700 hover:text-slate-900 hover:bg-white/80 bg-white sm:bg-transparent'
            }`}
          >
            <Phone className="w-4 h-4 shrink-0" />
            <span className="truncate">Hubungi Kami (/contact)</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('custom')}
            className={`px-3 py-2.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
              activeTab === 'custom'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-700 hover:text-slate-900 hover:bg-white/80 bg-white sm:bg-transparent'
            }`}
          >
            <Layers className="w-4 h-4 shrink-0" />
            <span className="truncate">Halaman Kustom</span>
            <span className={`px-1.5 py-0.5 text-[9px] rounded-full font-black shrink-0 ${
              activeTab === 'custom' ? 'bg-white/20 text-white' : 'bg-purple-100 text-purple-800'
            }`}>
              {customPages.length}
            </span>
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* TAB: KUSTOMISASI JUDUL TAB BROWSER PER HALAMAN */}
      {/* ========================================================================= */}
      {activeTab === 'tab_titles' && (
        <div className="space-y-6">
          {/* Header Explanation Card */}
          <div className="p-6 bg-gradient-to-r from-blue-900 via-slate-900 to-indigo-950 text-white rounded-2xl border border-blue-800/40 shadow-sm relative overflow-hidden">
            <div className="relative z-10 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-400/30 text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-blue-400" />
                  Kustomisasi Judul Tab Browser Aktif
                </span>
                <span className="text-slate-400 text-xs font-mono">
                  Situs: {settings?.siteName || 'ALY LAW FIRM'}
                </span>
              </div>
              <h3 className="text-lg sm:text-xl font-black text-white flex items-center gap-2">
                <Sliders className="w-5 h-5 text-blue-400" />
                <span>Kustomisasi Judul Tab Browser Setiap Halaman (&lt;title&gt;)</span>
              </h3>
              <p className="text-xs sm:text-sm text-slate-300 max-w-3xl leading-relaxed">
                Tulisan pada <strong>tab jendela browser</strong> (Google Chrome, Firefox, Safari, Edge) dapat Anda ubah dan sesuaikan secara fleksibel untuk setiap halaman website. Anda juga bisa mengatur teks akhiran (suffix) nama kantor atau membiarkannya mandiri.
              </p>
            </div>
          </div>

          {/* Live Browser Tab Preview Simulator */}
          <div className="bg-slate-900 p-5 rounded-2xl border border-slate-700 shadow-md space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-300 flex items-center gap-1.5">
                  <Eye className="w-4 h-4 text-emerald-400" />
                  <span>Simulasi Tampilan Tab Browser:</span>
                </span>
              </div>

              {/* Selector which tab to preview */}
              <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                <span className="text-slate-400 font-medium mr-1">Pilih Halaman:</span>
                {[
                  { key: 'home', label: 'Beranda' },
                  { key: 'blog', label: 'Blog' },
                  { key: 'products', label: 'Produk' },
                  { key: 'portfolio', label: 'Portofolio' },
                  { key: 'gallery', label: 'Galeri' },
                  { key: 'mediaShowcase', label: 'Media' },
                  { key: 'about', label: 'Tentang' },
                  { key: 'contact', label: 'Kontak' },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setActiveTabPreviewKey(item.key as any)}
                    className={`px-2.5 py-1 rounded-lg font-bold transition-all cursor-pointer ${
                      activeTabPreviewKey === item.key
                        ? 'bg-blue-600 text-white shadow-xs'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Browser Window Mockup */}
            <div className="bg-slate-800 rounded-xl overflow-hidden border border-slate-700 shadow-lg">
              {/* Browser Tab Bar */}
              <div className="bg-slate-950 px-3 pt-2.5 flex items-center gap-2 border-b border-slate-800">
                {/* Simulated Mac/Windows Window Controls */}
                <div className="flex items-center gap-1.5 mr-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-rose-500/80 inline-block" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80 inline-block" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80 inline-block" />
                </div>

                {/* The Active Simulated Tab */}
                <div className="bg-slate-800 text-slate-100 px-4 py-2 rounded-t-xl max-w-md flex items-center gap-2.5 shadow-sm border-t border-x border-slate-700">
                  {settings?.faviconUrl ? (
                    <img src={settings.faviconUrl} alt="Favicon" className="w-4 h-4 rounded-xs shrink-0 object-contain" />
                  ) : (
                    <span className="w-4 h-4 rounded-xs bg-emerald-600 text-white flex items-center justify-center text-[10px] font-black shrink-0">
                      ⚖️
                    </span>
                  )}
                  <span className="text-xs font-semibold truncate select-none text-slate-200">
                    {(() => {
                      const curVal = (tabTitlesForm[activeTabPreviewKey] || '').trim();
                      const suffix = tabTitlesForm.titleSuffix !== undefined ? tabTitlesForm.titleSuffix : ` | ${settings?.siteName || 'ALY LAW FIRM'}`;
                      if (curVal) {
                        return curVal.endsWith(suffix) ? curVal : `${curVal}${suffix}`;
                      }
                      const fallbacks: Record<string, string> = {
                        home: settings?.seo?.metaTitle || `${settings?.siteName || 'ALY LAW FIRM'} - ${settings?.tagline || 'Advokat & Konsultan Hukum'}`,
                        blog: `Artikel & Berita Hukum${suffix}`,
                        products: `Katalog Layanan Hukum${suffix}`,
                        portfolio: `Profil Project & Perkara${suffix}`,
                        gallery: `Galeri Foto & Video${suffix}`,
                        mediaShowcase: `Galeri Foto & Sorotan Visual${suffix}`,
                        about: `Tentang Kami${suffix}`,
                        contact: `Hubungi Kami${suffix}`,
                      };
                      return fallbacks[activeTabPreviewKey] || `Halaman Web${suffix}`;
                    })()}
                  </span>
                  <span className="text-slate-400 hover:text-white ml-auto text-xs font-bold cursor-pointer">×</span>
                </div>
              </div>

              {/* Browser Address Bar */}
              <div className="bg-slate-850 px-4 py-2 flex items-center gap-3 border-b border-slate-700/80 text-xs">
                <div className="flex items-center gap-1.5 text-slate-500">
                  <span className="cursor-pointer">←</span>
                  <span className="cursor-pointer">→</span>
                  <span className="cursor-pointer">⟳</span>
                </div>
                <div className="flex-1 bg-slate-900 px-3 py-1.5 rounded-lg border border-slate-700 text-slate-300 font-mono text-[11px] flex items-center gap-2">
                  <span className="text-emerald-400 font-bold">🔒</span>
                  <span className="text-slate-400">{window.location.origin}/</span>
                  <span className="text-emerald-300 font-bold">
                    {activeTabPreviewKey === 'home'
                      ? ''
                      : activeTabPreviewKey === 'blog'
                      ? (settings?.pageSlugs?.blogSlug || 'blog')
                      : activeTabPreviewKey === 'products'
                      ? (settings?.pageSlugs?.products || 'produk')
                      : activeTabPreviewKey === 'portfolio'
                      ? (settings?.pageSlugs?.portfolio || 'portofolio')
                      : activeTabPreviewKey === 'gallery'
                      ? (settings?.pageSlugs?.gallery || 'foto-video')
                      : activeTabPreviewKey === 'mediaShowcase'
                      ? (settings?.pageSlugs?.mediaShowcase || 'media-showcase')
                      : activeTabPreviewKey}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Form Settings Card */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
              <div>
                <h3 className="text-base font-extrabold text-slate-900 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-emerald-600" />
                  <span>Daftar Formulir Judul Tab Browser per Halaman</span>
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Isi judul tab untuk tiap halaman. Kosongkan jika ingin menggunakan judul default otomatis.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSaveTabTitles}
                  disabled={isSavingTabTitles}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {isSavingTabTitles ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Menyimpan...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span>Simpan Semua Judul Tab</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Global Suffix Setting */}
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
              <label className="text-xs font-bold text-slate-800 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5 text-blue-600" />
                  <span>Akhiran Judul Tab Otomatis (Global Title Suffix):</span>
                </span>
                <span className="text-[10px] text-slate-500 font-normal">
                  Ditambahkan otomatis di belakang judul halaman (bisa dikosongkan)
                </span>
              </label>
              <input
                type="text"
                placeholder="Contoh:  | ALY LAW FIRM & PARTNERS"
                value={tabTitlesForm.titleSuffix || ''}
                onChange={(e) => setTabTitlesForm({ ...tabTitlesForm, titleSuffix: e.target.value })}
                className="w-full px-3.5 py-2.5 text-xs bg-white border border-slate-300 rounded-xl font-semibold text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:outline-hidden"
              />
              <p className="text-[11px] text-slate-500">
                Contoh hasil di tab browser: <code>Artikel Hukum Terpercaya{tabTitlesForm.titleSuffix || ''}</code>
              </p>
            </div>

            {/* Individual Page Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* 1. Beranda */}
              <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2 hover:border-emerald-300 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span>1. Halaman Beranda (Home / &quot;/&quot;)</span>
                  </span>
                  <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded">/</span>
                </div>
                <input
                  type="text"
                  placeholder="Contoh: Kantor Advokat & Konsultan Hukum Ali Yusuf"
                  value={tabTitlesForm.home || ''}
                  onChange={(e) => setTabTitlesForm({ ...tabTitlesForm, home: e.target.value })}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-semibold focus:bg-white focus:ring-2 focus:ring-emerald-500"
                />
                <span className="text-[10px] text-slate-400 block">
                  Default jika kosong: {settings?.seo?.metaTitle || settings?.siteName || 'Beranda'}
                </span>
              </div>

              {/* 2. Blog */}
              <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2 hover:border-emerald-300 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    <span>2. Halaman Artikel & Blog Redaksi</span>
                  </span>
                  <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded">/{settings?.pageSlugs?.blogSlug || 'blog'}</span>
                </div>
                <input
                  type="text"
                  placeholder="Contoh: Artikel, Analisis & Opini Hukum Terpercaya"
                  value={tabTitlesForm.blog || ''}
                  onChange={(e) => setTabTitlesForm({ ...tabTitlesForm, blog: e.target.value })}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-semibold focus:bg-white focus:ring-2 focus:ring-emerald-500"
                />
                <span className="text-[10px] text-slate-400 block">
                  Default jika kosong: Artikel & Berita Hukum
                </span>
              </div>

              {/* 3. Produk */}
              <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2 hover:border-emerald-300 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    <span>3. Halaman Layanan & Produk</span>
                  </span>
                  <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded">/{settings?.pageSlugs?.products || 'produk'}</span>
                </div>
                <input
                  type="text"
                  placeholder="Contoh: Layanan Konsultasi & Advokasi Hukum"
                  value={tabTitlesForm.products || ''}
                  onChange={(e) => setTabTitlesForm({ ...tabTitlesForm, products: e.target.value })}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-semibold focus:bg-white focus:ring-2 focus:ring-emerald-500"
                />
                <span className="text-[10px] text-slate-400 block">
                  Default jika kosong: Judul Grid Layanan & Produk
                </span>
              </div>

              {/* 4. Portofolio */}
              <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2 hover:border-emerald-300 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-purple-500" />
                    <span>4. Halaman Portofolio & Perkara Klien</span>
                  </span>
                  <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded">/{settings?.pageSlugs?.portfolio || 'portofolio'}</span>
                </div>
                <input
                  type="text"
                  placeholder="Contoh: Rekam Jejak Perkara & Pengalaman Klien"
                  value={tabTitlesForm.portfolio || ''}
                  onChange={(e) => setTabTitlesForm({ ...tabTitlesForm, portfolio: e.target.value })}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-semibold focus:bg-white focus:ring-2 focus:ring-emerald-500"
                />
                <span className="text-[10px] text-slate-400 block">
                  Default jika kosong: Judul Grid Portofolio Proyek
                </span>
              </div>

              {/* 5. Galeri */}
              <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2 hover:border-emerald-300 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-teal-500" />
                    <span>5. Halaman Galeri Foto & Video</span>
                  </span>
                  <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded">/{settings?.pageSlugs?.gallery || 'foto-video'}</span>
                </div>
                <input
                  type="text"
                  placeholder="Contoh: Galeri Dokumentasi Kegiatan & Video"
                  value={tabTitlesForm.gallery || ''}
                  onChange={(e) => setTabTitlesForm({ ...tabTitlesForm, gallery: e.target.value })}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-semibold focus:bg-white focus:ring-2 focus:ring-emerald-500"
                />
                <span className="text-[10px] text-slate-400 block">
                  Default jika kosong: Galeri Foto & Video
                </span>
              </div>

              {/* 6. Media Showcase */}
              <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2 hover:border-emerald-300 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-sky-500" />
                    <span>6. Halaman Media & Foto Showcase</span>
                  </span>
                  <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded">/{settings?.pageSlugs?.mediaShowcase || 'media-showcase'}</span>
                </div>
                <input
                  type="text"
                  placeholder="Contoh: Liputan Media & Sorotan Visual"
                  value={tabTitlesForm.mediaShowcase || ''}
                  onChange={(e) => setTabTitlesForm({ ...tabTitlesForm, mediaShowcase: e.target.value })}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-semibold focus:bg-white focus:ring-2 focus:ring-emerald-500"
                />
                <span className="text-[10px] text-slate-400 block">
                  Default jika kosong: Galeri Foto & Sorotan Visual
                </span>
              </div>

              {/* 7. Tentang Kami */}
              <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2 hover:border-emerald-300 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-rose-500" />
                    <span>7. Halaman Tentang Kami</span>
                  </span>
                  <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded">/about</span>
                </div>
                <input
                  type="text"
                  placeholder="Contoh: Profil Advokat, Keahlian & Nilai Inti"
                  value={tabTitlesForm.about || ''}
                  onChange={(e) => setTabTitlesForm({ ...tabTitlesForm, about: e.target.value })}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-semibold focus:bg-white focus:ring-2 focus:ring-emerald-500"
                />
                <span className="text-[10px] text-slate-400 block">
                  Default jika kosong: Tentang Kami
                </span>
              </div>

              {/* 8. Hubungi Kami */}
              <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2 hover:border-emerald-300 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    <span>8. Halaman Hubungi Kami</span>
                  </span>
                  <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded">/contact</span>
                </div>
                <input
                  type="text"
                  placeholder="Contoh: Konsultasi & Hotline Kantor Hukum"
                  value={tabTitlesForm.contact || ''}
                  onChange={(e) => setTabTitlesForm({ ...tabTitlesForm, contact: e.target.value })}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-semibold focus:bg-white focus:ring-2 focus:ring-emerald-500"
                />
                <span className="text-[10px] text-slate-400 block">
                  Default jika kosong: Hubungi Kami
                </span>
              </div>
            </div>

            {/* Save Button Bottom */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
              <span className="text-xs text-slate-500">
                {tabTitlesSavedSuccess && (
                  <span className="text-emerald-700 font-bold flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>Perubahan judul tab browser berhasil tersimpan permanen!</span>
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={handleSaveTabTitles}
                disabled={isSavingTabTitles}
                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>Simpan Semua Judul Tab</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 0: PENGATURAN URL & SLUG SEMUA HALAMAN (CENTRALIZED URL MANAGER) */}
      {/* ========================================================================= */}
      {activeTab === 'urls' && (
        <div className="space-y-6">
          {/* Header Card Penjelasan */}
          <div className="p-5 bg-gradient-to-r from-emerald-900 via-slate-900 to-teal-950 text-white rounded-2xl border border-emerald-800/40 shadow-sm relative overflow-hidden">
            <div className="relative z-10 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  Sistem URL & Permalink Dinamis Aktif
                </span>
                <span className="text-slate-400 text-xs font-mono">
                  Domain: {window.location.origin}
                </span>
              </div>
              <h3 className="text-lg sm:text-xl font-black text-white flex items-center gap-2">
                <Globe className="w-5 h-5 text-emerald-400" />
                <span>Pusat Pengaturan URL & Slug Semua Halaman Website</span>
              </h3>
              <p className="text-xs sm:text-sm text-slate-300 max-w-3xl leading-relaxed">
                <strong>Ya, seluruh URL dan Slug halaman website dapat Anda ubah dan sesuaikan langsung dari Dashboard ini!</strong>{' '}
                Ketika Anda mengubah slug (misalnya <code className="bg-white/10 px-1 py-0.5 rounded text-emerald-300">/produk</code> diubah menjadi <code className="bg-white/10 px-1 py-0.5 rounded text-emerald-300">/layanan-kami</code>), sistem secara otomatis menyelaraskan menu navigasi header, tombol CTA beranda, routing browser, serta meta link.
              </p>
            </div>
          </div>

          {/* Form Kustomisasi Slug Halaman Utama */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
              <div>
                <h4 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Sliders className="w-4 h-4 text-emerald-600" />
                  <span>Kustomisasi Slug URL Halaman Utama & Katalog</span>
                </h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  Ketik nama slug yang diinginkan (hanya huruf kecil, angka, dan tanda strip).
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleSaveAllSlugs}
                  disabled={isSavingSlugs}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-xs rounded-xl shadow-xs transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  <span>{isSavingSlugs ? 'Menyimpan URL...' : 'Simpan Semua Pengaturan Slug URL'}</span>
                </button>
              </div>
            </div>

            {slugsSavedSuccess && (
              <div className="p-3 bg-emerald-50 border border-emerald-300 rounded-xl flex items-center gap-2 text-xs font-bold text-emerald-800 animate-in fade-in duration-200">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Pengaturan Slug URL berhasil disimpan dan telah aktif di seluruh sistem website!</span>
              </div>
            )}

            {/* Grid 5 Halaman Utama */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 1. Layanan & Produk */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-emerald-300 transition-colors space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="p-1.5 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-black">
                      🛍️
                    </span>
                    <span className="text-xs font-bold text-slate-900">Katalog Produk & Layanan</span>
                  </div>
                  <span className="text-[10px] bg-slate-200 text-slate-700 px-2 py-0.5 rounded font-mono font-bold">
                    Default: /produk
                  </span>
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-semibold text-slate-600">Slug URL Halaman:</label>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-mono text-slate-400 font-bold">/</span>
                    <input
                      type="text"
                      placeholder="produk atau layanan-kami"
                      value={slugsForm.products}
                      onChange={(e) => setSlugsForm((prev) => ({ ...prev, products: e.target.value }))}
                      className="w-full text-xs font-mono font-bold text-slate-900 bg-white border border-slate-300 px-3 py-1.5 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-slate-200/60 text-[10px]">
                  <span className="font-mono text-emerald-700 truncate max-w-[220px]">
                    {window.location.origin}/{slugsForm.products || 'produk'}
                  </span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => copyPageUrl(`/${slugsForm.products || 'produk'}`, 'Katalog Produk')}
                      className="text-slate-600 hover:text-emerald-700 font-bold flex items-center gap-1 cursor-pointer"
                      title="Salin Tautan"
                    >
                      <Copy className="w-3 h-3" />
                      <span>Salin</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('products')}
                      className="text-emerald-700 hover:text-emerald-900 font-bold flex items-center gap-1 cursor-pointer"
                      title="Buka Halaman"
                    >
                      <ExternalLink className="w-3 h-3" />
                      <span>Buka</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* 2. Artikel & Blog */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-emerald-300 transition-colors space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="p-1.5 bg-sky-100 text-sky-700 rounded-lg text-xs font-black">
                      📰
                    </span>
                    <span className="text-xs font-bold text-slate-900">Artikel & Berita / Blog</span>
                  </div>
                  <span className="text-[10px] bg-slate-200 text-slate-700 px-2 py-0.5 rounded font-mono font-bold">
                    Default: /blog
                  </span>
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-semibold text-slate-600">Slug URL Halaman:</label>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-mono text-slate-400 font-bold">/</span>
                    <input
                      type="text"
                      placeholder="blog atau artikel"
                      value={slugsForm.articles}
                      onChange={(e) => setSlugsForm((prev) => ({ ...prev, articles: e.target.value }))}
                      className="w-full text-xs font-mono font-bold text-slate-900 bg-white border border-slate-300 px-3 py-1.5 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-slate-200/60 text-[10px]">
                  <span className="font-mono text-emerald-700 truncate max-w-[220px]">
                    {window.location.origin}/{slugsForm.articles || 'blog'}
                  </span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => copyPageUrl(`/${slugsForm.articles || 'blog'}`, 'Halaman Artikel')}
                      className="text-slate-600 hover:text-emerald-700 font-bold flex items-center gap-1 cursor-pointer"
                    >
                      <Copy className="w-3 h-3" />
                      <span>Salin</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('blog')}
                      className="text-emerald-700 hover:text-emerald-900 font-bold flex items-center gap-1 cursor-pointer"
                    >
                      <ExternalLink className="w-3 h-3" />
                      <span>Buka</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* 3. Portofolio */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-emerald-300 transition-colors space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="p-1.5 bg-amber-100 text-amber-700 rounded-lg text-xs font-black">
                      💼
                    </span>
                    <span className="text-xs font-bold text-slate-900">Portofolio</span>
                  </div>
                  <span className="text-[10px] bg-slate-200 text-slate-700 px-2 py-0.5 rounded font-mono font-bold">
                    Default: /portofolio
                  </span>
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-semibold text-slate-600">Slug URL Halaman:</label>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-mono text-slate-400 font-bold">/</span>
                    <input
                      type="text"
                      placeholder="portofolio atau proyek"
                      value={slugsForm.portfolio}
                      onChange={(e) => setSlugsForm((prev) => ({ ...prev, portfolio: e.target.value }))}
                      className="w-full text-xs font-mono font-bold text-slate-900 bg-white border border-slate-300 px-3 py-1.5 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-slate-200/60 text-[10px]">
                  <span className="font-mono text-emerald-700 truncate max-w-[220px]">
                    {window.location.origin}/{slugsForm.portfolio || 'portofolio'}
                  </span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => copyPageUrl(`/${slugsForm.portfolio || 'portofolio'}`, 'Halaman Portofolio')}
                      className="text-slate-600 hover:text-emerald-700 font-bold flex items-center gap-1 cursor-pointer"
                    >
                      <Copy className="w-3 h-3" />
                      <span>Salin</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('portfolio')}
                      className="text-emerald-700 hover:text-emerald-900 font-bold flex items-center gap-1 cursor-pointer"
                    >
                      <ExternalLink className="w-3 h-3" />
                      <span>Buka</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* 4. Galeri Video & Foto */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-emerald-300 transition-colors space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="p-1.5 bg-rose-100 text-rose-700 rounded-lg text-xs font-black">
                      🎬
                    </span>
                    <span className="text-xs font-bold text-slate-900">Galeri Video & Foto</span>
                  </div>
                  <span className="text-[10px] bg-slate-200 text-slate-700 px-2 py-0.5 rounded font-mono font-bold">
                    Default: /foto-video
                  </span>
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-semibold text-slate-600">Slug URL Halaman:</label>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-mono text-slate-400 font-bold">/</span>
                    <input
                      type="text"
                      placeholder="foto-video atau galeri"
                      value={slugsForm.gallery}
                      onChange={(e) => setSlugsForm((prev) => ({ ...prev, gallery: e.target.value }))}
                      className="w-full text-xs font-mono font-bold text-slate-900 bg-white border border-slate-300 px-3 py-1.5 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-slate-200/60 text-[10px]">
                  <span className="font-mono text-emerald-700 truncate max-w-[220px]">
                    {window.location.origin}/{slugsForm.gallery || 'foto-video'}
                  </span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => copyPageUrl(`/${slugsForm.gallery || 'foto-video'}`, 'Halaman Galeri')}
                      className="text-slate-600 hover:text-emerald-700 font-bold flex items-center gap-1 cursor-pointer"
                    >
                      <Copy className="w-3 h-3" />
                      <span>Salin</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('gallery')}
                      className="text-emerald-700 hover:text-emerald-900 font-bold flex items-center gap-1 cursor-pointer"
                    >
                      <ExternalLink className="w-3 h-3" />
                      <span>Buka</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* 5. Media Showcase & Foto */}
              <div className="p-4 bg-sky-50/70 rounded-xl border border-sky-200 hover:border-sky-400 transition-colors space-y-2.5 md:col-span-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="p-1.5 bg-sky-200 text-sky-800 rounded-lg text-xs font-black">
                      📸
                    </span>
                    <span className="text-xs font-bold text-slate-900">Media & Foto Showcase Catalog</span>
                  </div>
                  <span className="text-[10px] bg-sky-200/80 text-sky-900 px-2 py-0.5 rounded font-mono font-bold">
                    Default: /media-showcase
                  </span>
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-semibold text-slate-600">Slug URL Halaman Showcase:</label>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-mono text-slate-400 font-bold">/</span>
                    <input
                      type="text"
                      placeholder="media-showcase atau sorotan-foto"
                      value={slugsForm.mediaShowcase}
                      onChange={(e) => setSlugsForm((prev) => ({ ...prev, mediaShowcase: e.target.value }))}
                      className="w-full text-xs font-mono font-bold text-slate-900 bg-white border border-sky-300 px-3 py-1.5 rounded-lg focus:ring-2 focus:ring-sky-500 focus:outline-none"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-sky-200/60 text-[10px]">
                  <span className="font-mono text-sky-800 truncate">
                    {window.location.origin}/{slugsForm.mediaShowcase || 'media-showcase'}
                  </span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => copyPageUrl(`/${slugsForm.mediaShowcase || 'media-showcase'}`, 'Halaman Media Showcase')}
                      className="text-slate-600 hover:text-sky-800 font-bold flex items-center gap-1 cursor-pointer"
                    >
                      <Copy className="w-3 h-3" />
                      <span>Salin</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('media-showcase')}
                      className="text-sky-800 hover:text-sky-950 font-bold flex items-center gap-1 cursor-pointer"
                    >
                      <ExternalLink className="w-3 h-3" />
                      <span>Buka</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Tombol Simpan Bawah */}
            <div className="pt-2 flex justify-end">
              <button
                type="button"
                onClick={handleSaveAllSlugs}
                disabled={isSavingSlugs}
                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-xs rounded-xl shadow-xs transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>{isSavingSlugs ? 'Menyimpan URL...' : 'Simpan Semua Pengaturan Slug URL'}</span>
              </button>
            </div>
          </div>

          {/* Direktori Lengkap Semua URL Halaman Website (Live URL Directory) */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
              <div>
                <h4 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <LinkIcon className="w-4 h-4 text-emerald-600" />
                  <span>Direktori Lengkap Semua URL Halaman Website</span>
                </h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  Daftar seluruh tautan URL yang aktif pada website Anda. Anda dapat menyalin tautan atau membukanya langsung.
                </p>
              </div>

              {/* Filter / Cari URL */}
              <div className="relative w-full sm:w-64">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Cari nama halaman / URL..."
                  value={urlSearchQuery}
                  onChange={(e) => setUrlSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:bg-white"
                />
              </div>
            </div>

            {/* Tabel Direktori URL */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-slate-600 font-bold uppercase text-[10px] tracking-wider">
                    <th className="py-2.5 px-3">Halaman / Fitur</th>
                    <th className="py-2.5 px-3">Tipe</th>
                    <th className="py-2.5 px-3">Path / Slug</th>
                    <th className="py-2.5 px-3">URL Publik Penuh</th>
                    <th className="py-2.5 px-3 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[
                    {
                      name: 'Beranda (Homepage)',
                      icon: '🏠',
                      type: 'Sistem',
                      badgeColor: 'bg-slate-100 text-slate-700',
                      path: '/',
                      fullUrl: `${window.location.origin}/`,
                      onClick: () => navigate('home'),
                    },
                    {
                      name: 'Katalog Layanan & Produk',
                      icon: '🛍️',
                      type: 'Katalog',
                      badgeColor: 'bg-emerald-100 text-emerald-800',
                      path: `/${slugsForm.products || 'produk'}`,
                      fullUrl: `${window.location.origin}/${slugsForm.products || 'produk'}`,
                      onClick: () => navigate('products'),
                    },
                    {
                      name: 'Artikel & Berita / Blog',
                      icon: '📰',
                      type: 'Katalog',
                      badgeColor: 'bg-sky-100 text-sky-800',
                      path: `/${slugsForm.articles || 'blog'}`,
                      fullUrl: `${window.location.origin}/${slugsForm.articles || 'blog'}`,
                      onClick: () => navigate('blog'),
                    },
                    {
                      name: 'Portofolio',
                      icon: '💼',
                      type: 'Katalog',
                      badgeColor: 'bg-amber-100 text-amber-800',
                      path: `/${slugsForm.portfolio || 'portofolio'}`,
                      fullUrl: `${window.location.origin}/${slugsForm.portfolio || 'portofolio'}`,
                      onClick: () => navigate('portfolio'),
                    },
                    {
                      name: 'Galeri Video & Foto',
                      icon: '🎬',
                      type: 'Katalog',
                      badgeColor: 'bg-rose-100 text-rose-800',
                      path: `/${slugsForm.gallery || 'foto-video'}`,
                      fullUrl: `${window.location.origin}/${slugsForm.gallery || 'foto-video'}`,
                      onClick: () => navigate('gallery'),
                    },
                    {
                      name: 'Media & Foto Showcase Catalog',
                      icon: '📸',
                      type: 'Katalog',
                      badgeColor: 'bg-sky-100 text-sky-800',
                      path: `/${slugsForm.mediaShowcase || 'media-showcase'}`,
                      fullUrl: `${window.location.origin}/${slugsForm.mediaShowcase || 'media-showcase'}`,
                      onClick: () => navigate('media-showcase'),
                    },
                    {
                      name: 'Tentang Kami',
                      icon: '🏢',
                      type: 'Halaman Statis',
                      badgeColor: 'bg-indigo-100 text-indigo-800',
                      path: '/about',
                      fullUrl: `${window.location.origin}/about`,
                      onClick: () => navigate('about'),
                    },
                    {
                      name: 'Hubungi Kami & Peta',
                      icon: '📞',
                      type: 'Halaman Statis',
                      badgeColor: 'bg-indigo-100 text-indigo-800',
                      path: '/contact',
                      fullUrl: `${window.location.origin}/contact`,
                      onClick: () => navigate('contact'),
                    },
                    ...customPages.map((cp) => ({
                      name: cp.title,
                      icon: '📄',
                      type: 'Halaman Kustom',
                      badgeColor: 'bg-purple-100 text-purple-800',
                      path: `/page/${cp.slug}`,
                      fullUrl: `${window.location.origin}/page/${cp.slug}`,
                      onClick: () => navigate('custom-page', cp.slug),
                    })),
                  ]
                    .filter((item) =>
                      urlSearchQuery
                        ? item.name.toLowerCase().includes(urlSearchQuery.toLowerCase()) ||
                          item.path.toLowerCase().includes(urlSearchQuery.toLowerCase()) ||
                          item.type.toLowerCase().includes(urlSearchQuery.toLowerCase())
                        : true
                    )
                    .map((item, idx) => (
                      <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-2.5 px-3 font-semibold text-slate-800 flex items-center gap-2">
                          <span>{item.icon}</span>
                          <span>{item.name}</span>
                        </td>
                        <td className="py-2.5 px-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${item.badgeColor}`}>
                            {item.type}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 font-mono font-bold text-slate-700">
                          {item.path}
                        </td>
                        <td className="py-2.5 px-3 font-mono text-slate-500 max-w-xs truncate">
                          {item.fullUrl}
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => copyPageUrl(item.path, item.name)}
                              className="px-2 py-1 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 rounded text-[11px] font-bold flex items-center gap-1 shadow-2xs transition-colors cursor-pointer"
                              title="Salin Tautan URL"
                            >
                              <Copy className="w-3 h-3 text-slate-500" />
                              <span>Salin</span>
                            </button>
                            <button
                              type="button"
                              onClick={item.onClick}
                              className="px-2 py-1 bg-slate-800 hover:bg-slate-900 text-white rounded text-[11px] font-bold flex items-center gap-1 shadow-2xs transition-colors cursor-pointer"
                              title="Buka Halaman"
                            >
                              <Eye className="w-3 h-3" />
                              <span>Buka</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Panduan: Di Mana Saja Anda Bisa Menyetting URL dari Dashboard */}
          <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
            <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
              <Info className="w-4 h-4 text-emerald-600" />
              <span>Panduan Lengkap: Di Mana Saja URL Bisa Disetting dari Dashboard?</span>
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
              <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-1">
                <span className="font-bold text-emerald-800 flex items-center gap-1.5">
                  1. Tab URL Ini (Pusat URL)
                </span>
                <p className="text-slate-600 text-[11px]">
                  Mengubah slug utama produk, artikel, portofolio, galeri, dan media showcase sekaligus.
                </p>
              </div>

              <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-1">
                <span className="font-bold text-emerald-800 flex items-center gap-1.5">
                  2. Menu Layanan & Produk
                </span>
                <p className="text-slate-600 text-[11px]">
                  Buka tab <strong>Pengaturan Grid & Slug URL</strong> untuk mengubah URL slug katalog produk.
                </p>
              </div>

              <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-1">
                <span className="font-bold text-emerald-800 flex items-center gap-1.5">
                  3. Menu Artikel & Berita
                </span>
                <p className="text-slate-600 text-[11px]">
                  Tab <strong>Pengaturan Grid & Slug URL</strong> (katalog blog), serta kolom <strong>Slug URL</strong> spesifik per artikel saat menulis.
                </p>
              </div>

              <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-1">
                <span className="font-bold text-emerald-800 flex items-center gap-1.5">
                  4. Menu Portofolio Proyek
                </span>
                <p className="text-slate-600 text-[11px]">
                  Tab <strong>Pengaturan Grid & Slug URL</strong>, serta kolom <strong>Slug URL Khusus Portofolio (Permalink Mandiri)</strong> per proyek.
                </p>
              </div>

              <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-1">
                <span className="font-bold text-emerald-800 flex items-center gap-1.5">
                  5. Menu Galeri Foto & Video
                </span>
                <p className="text-slate-600 text-[11px]">
                  Buka tab <strong>Pengaturan Grid & Slug URL</strong> untuk mengubah URL slug galeri video & foto.
                </p>
              </div>

              <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-1">
                <span className="font-bold text-emerald-800 flex items-center gap-1.5">
                  6. Tab Halaman Kustom
                </span>
                <p className="text-slate-600 text-[11px]">
                  Saat membuat halaman baru di tab <strong>Halaman Kustom</strong>, Anda bebas menentukan slug URL-nya.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 1: TENTANG KAMI (ABOUT US) */}
      {/* ========================================================================= */}
      {activeTab === 'about' && (
        <div className="space-y-6">
          {/* Top Quick URL Bar */}
          <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs text-emerald-950">
              <Globe className="w-4 h-4 text-emerald-700 shrink-0" />
              <span>
                URL Publik Halaman:{' '}
                <code className="bg-emerald-100/80 px-2 py-0.5 rounded font-mono font-bold text-emerald-900">
                  /about
                </code>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => copyPageUrl('/about', 'Tentang Kami')}
                className="px-3 py-1.5 bg-white hover:bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-2xs transition-colors"
              >
                <Copy className="w-3.5 h-3.5" />
                <span>Salin URL /about</span>
              </button>
              <button
                onClick={() => navigate('about')}
                className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-2xs transition-colors"
              >
                <Eye className="w-3.5 h-3.5" />
                <span>Buka Halaman Live</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left: Editor Inputs */}
            <div className="lg:col-span-8 space-y-6">
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-5">
                <h3 className="text-base font-bold text-slate-900 pb-3 border-b border-slate-100 flex items-center gap-2">
                  <Edit3 className="w-4 h-4 text-emerald-600" />
                  <span>Konten & Identitas Halaman Tentang Kami</span>
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">
                      Judul Utama Halaman
                    </label>
                    <input
                      type="text"
                      value={aboutData.title}
                      onChange={(e) => setAboutData({ ...aboutData, title: e.target.value })}
                      placeholder="Tentang Kami"
                      className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 font-medium text-slate-800"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">
                      Badge / Label Kecil di Atas Judul
                    </label>
                    <input
                      type="text"
                      value={aboutData.badge || ''}
                      onChange={(e) => setAboutData({ ...aboutData, badge: e.target.value })}
                      placeholder="Profil Perusahaan & Nilai Inti"
                      className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 font-medium text-slate-800"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Subjudul / Slogan Pengantar
                  </label>
                  <input
                    type="text"
                    value={aboutData.subtitle || ''}
                    onChange={(e) => setAboutData({ ...aboutData, subtitle: e.target.value })}
                    placeholder="Membangun sinergi pengalaman terbaik antara alam, kreativitas..."
                    className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 font-medium text-slate-800"
                  />
                </div>

                {/* Cover Image */}
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Foto Sampul / Banner Profil
                  </label>
                  <div className="flex flex-col sm:flex-row items-center gap-3">
                    <input
                      type="text"
                      value={aboutData.coverImage}
                      onChange={(e) => setAboutData({ ...aboutData, coverImage: e.target.value })}
                      placeholder="https://..."
                      className="w-full flex-1 px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 text-slate-800"
                    />
                    <label className="cursor-pointer px-4 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold flex items-center gap-2 shrink-0 transition-colors">
                      <Upload className="w-3.5 h-3.5" />
                      <span>{uploadingImage ? 'Mengompres...' : 'Unggah Foto'}</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'about')}
                        className="hidden"
                      />
                    </label>
                  </div>
                  {aboutData.coverImage && (
                    <div className="mt-3 rounded-xl overflow-hidden aspect-16/7 bg-slate-900 max-h-48 border border-slate-200">
                      <img
                        src={aboutData.coverImage}
                        alt="Preview Cover"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>

                {/* Markdown Content */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-bold text-slate-700">
                      Isi Artikel / Cerita Profil (Mendukung Format Markdown)
                    </label>
                    <span className="text-[11px] text-slate-400">Gunakan ## Judul, **tebal**, - daftar poin</span>
                  </div>
                  <textarea
                    rows={12}
                    value={aboutData.content}
                    onChange={(e) => setAboutData({ ...aboutData, content: e.target.value })}
                    placeholder="Tuliskan sejarah berdirinya perusahaan, visi, misi, dan komitmen..."
                    className="w-full p-4 text-xs font-mono bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 text-slate-800 leading-relaxed"
                  />
                </div>
              </div>

              {/* Value Items / 3 Pilar Keunggulan */}
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                  <div>
                    <h3 className="text-base font-bold text-slate-900">
                      Nilai-Nilai Inti & Keunggulan Perusahaan (Values Grid)
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Kotak pilar yang tampil di bagian bawah halaman Tentang Kami
                    </p>
                  </div>
                  <button
                    onClick={addValueItem}
                    className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Tambah Pilar</span>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {aboutData.values?.map((val, idx) => (
                    <div
                      key={val.id || idx}
                      className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3 relative group"
                    >
                      <button
                        onClick={() => removeValueItem(val.id)}
                        className="absolute top-3 right-3 text-slate-400 hover:text-red-600 transition-colors p-1"
                        title="Hapus Nilai Ini"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      <div>
                        <label className="text-[11px] font-bold text-slate-500 block mb-1">
                          Ikon / Tipe
                        </label>
                        <select
                          value={val.icon}
                          onChange={(e) => updateValueItem(val.id, 'icon', e.target.value)}
                          className="w-full px-2.5 py-1.5 text-xs bg-white border border-slate-300 rounded-lg text-slate-700"
                        >
                          <option value="Compass">🧭 Compass (Eksplorasi)</option>
                          <option value="HeartHandshake">🤝 HeartHandshake (Sinergi & UMKM)</option>
                          <option value="Award">🏆 Award (Kualitas & Prestasi)</option>
                          <option value="ShieldCheck">🛡️ ShieldCheck (Keamanan)</option>
                          <option value="Star">⭐ Star (Kreativitas & Unggulan)</option>
                          <option value="Clock">⏱️ Clock (Respon Cepat)</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[11px] font-bold text-slate-500 block mb-1">
                          Judul Pilar
                        </label>
                        <input
                          type="text"
                          value={val.title}
                          onChange={(e) => updateValueItem(val.id, 'title', e.target.value)}
                          className="w-full px-2.5 py-1.5 text-xs bg-white border border-slate-300 rounded-lg text-slate-800 font-semibold"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] font-bold text-slate-500 block mb-1">
                          Deskripsi Singkat
                        </label>
                        <textarea
                          rows={2}
                          value={val.description}
                          onChange={(e) => updateValueItem(val.id, 'description', e.target.value)}
                          className="w-full px-2.5 py-1.5 text-xs bg-white border border-slate-300 rounded-lg text-slate-700"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Live Markdown Preview & SEO */}
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4">
                <h4 className="font-bold text-sm text-slate-900 pb-2 border-b border-slate-100 flex items-center justify-between">
                  <span>Pratinjau Langsung Konten</span>
                  <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded font-bold">
                    Live Render
                  </span>
                </h4>
                <div className="max-h-[500px] overflow-y-auto pr-1 text-xs text-slate-700 leading-relaxed border border-slate-100 p-4 rounded-xl bg-slate-50/50">
                  <h2 className="text-base font-extrabold text-slate-900 mb-1">{aboutData.title}</h2>
                  <p className="text-xs text-slate-500 mb-4">{aboutData.subtitle}</p>
                  <div className="article-prose">
                    <ReactMarkdown>{aboutData.content}</ReactMarkdown>
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    onClick={handleSaveAbout}
                    disabled={isSaving}
                    className="w-full py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold shadow-md flex items-center justify-center gap-2 transition-all"
                  >
                    <Check className="w-4 h-4" />
                    <span>{isSaving ? 'Menyimpan...' : 'Simpan Halaman Tentang Kami'}</span>
                  </button>
                </div>
              </div>

              {/* SEO Meta Box */}
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4">
                <h4 className="font-bold text-sm text-slate-900 pb-2 border-b border-slate-100">
                  Pengaturan SEO Halaman Tentang Kami
                </h4>
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Meta Title (Opsional)
                  </label>
                  <input
                    type="text"
                    value={aboutData.metaTitle || ''}
                    onChange={(e) => setAboutData({ ...aboutData, metaTitle: e.target.value })}
                    placeholder="Tentang Kami | Bellva Adventure & EO"
                    className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-800"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Meta Description (Opsional)
                  </label>
                  <textarea
                    rows={3}
                    value={aboutData.metaDescription || ''}
                    onChange={(e) => setAboutData({ ...aboutData, metaDescription: e.target.value })}
                    placeholder="Profil lengkap, rekam jejak, dan visi misi perusahaan..."
                    className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-800"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: HUBUNGI KAMI (CONTACT US) */}
      {/* ========================================================================= */}
      {activeTab === 'contact' && (
        <div className="space-y-6">
          {/* Top Quick URL Bar */}
          <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs text-emerald-950">
              <Globe className="w-4 h-4 text-emerald-700 shrink-0" />
              <span>
                URL Publik Halaman:{' '}
                <code className="bg-emerald-100/80 px-2 py-0.5 rounded font-mono font-bold text-emerald-900">
                  /contact
                </code>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => copyPageUrl('/contact', 'Hubungi Kami')}
                className="px-3 py-1.5 bg-white hover:bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-2xs transition-colors"
              >
                <Copy className="w-3.5 h-3.5" />
                <span>Salin URL /contact</span>
              </button>
              <button
                onClick={() => navigate('contact')}
                className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-2xs transition-colors"
              >
                <Eye className="w-3.5 h-3.5" />
                <span>Buka Halaman Live</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left: Main Form */}
            <div className="lg:col-span-7 space-y-6">
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-5">
                <h3 className="text-base font-bold text-slate-900 pb-3 border-b border-slate-100 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-emerald-600" />
                  <span>Pengaturan Teks & Judul Hubungi Kami</span>
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">
                      Judul Utama Halaman
                    </label>
                    <input
                      type="text"
                      value={contactData.title}
                      onChange={(e) => setContactData({ ...contactData, title: e.target.value })}
                      placeholder="Hubungi Kami"
                      className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 font-medium text-slate-800"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">
                      Badge / Label Kecil
                    </label>
                    <input
                      type="text"
                      value={contactData.badge || ''}
                      onChange={(e) => setContactData({ ...contactData, badge: e.target.value })}
                      placeholder="Konsultasi & Reservasi 24/7"
                      className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 font-medium text-slate-800"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Subjudul Pengantar
                  </label>
                  <input
                    type="text"
                    value={contactData.subtitle || ''}
                    onChange={(e) => setContactData({ ...contactData, subtitle: e.target.value })}
                    placeholder="Konsultasikan kebutuhan acara, kemitraan..."
                    className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 font-medium text-slate-800"
                  />
                </div>

                {/* Google Maps Embed URL */}
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Google Maps Embed URL (Iframe)
                  </label>
                  <input
                    type="text"
                    value={contactData.mapEmbedUrl}
                    onChange={(e) => setContactData({ ...contactData, mapEmbedUrl: e.target.value })}
                    placeholder="https://maps.google.com/maps?q=...&output=embed"
                    className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 text-slate-800 font-mono"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">
                    Masukkan URL embed peta atau link pencarian Google Maps untuk menampilkan peta interaktif kantor.
                  </p>
                </div>

                {/* Markdown Notes */}
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Teks Catatan / Informasi Tambahan (Markdown)
                  </label>
                  <textarea
                    rows={6}
                    value={contactData.content}
                    onChange={(e) => setContactData({ ...contactData, content: e.target.value })}
                    placeholder="Informasi jam kerja, prosedur pemesanan, atau rute akses..."
                    className="w-full p-3.5 text-xs font-mono bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 text-slate-800"
                  />
                </div>
              </div>
            </div>

            {/* Right: Map Preview & Contacts Info */}
            <div className="lg:col-span-5 space-y-6">
              {/* Map Preview Box */}
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-3">
                <h4 className="font-bold text-sm text-slate-900 pb-2 border-b border-slate-100 flex items-center justify-between">
                  <span>Pratinjau Peta Google Maps</span>
                  <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded font-bold">
                    Iframe Preview
                  </span>
                </h4>
                {contactData.mapEmbedUrl ? (
                  <div className="rounded-xl overflow-hidden border border-slate-200 h-56 bg-slate-100 shadow-inner">
                    <iframe
                      src={parseGoogleMapsEmbedUrl(contactData.mapEmbedUrl, settings?.contact?.address)}
                      title="Lokasi Kantor"
                      className="w-full h-full border-0"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="h-44 bg-slate-100 rounded-xl flex items-center justify-center text-xs text-slate-400 border border-dashed border-slate-300">
                    Masukkan Google Maps Embed URL untuk melihat pratinjau
                  </div>
                )}

                <div className="pt-3">
                  <button
                    onClick={handleSaveContact}
                    disabled={isSaving}
                    className="w-full py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold shadow-md flex items-center justify-center gap-2 transition-all"
                  >
                    <Check className="w-4 h-4" />
                    <span>{isSaving ? 'Menyimpan...' : 'Simpan Halaman Hubungi Kami'}</span>
                  </button>
                </div>
              </div>

              {/* Quick Contact Info Reference */}
              <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-3 text-xs">
                <h4 className="font-bold text-slate-800 flex items-center gap-2">
                  <Info className="w-4 h-4 text-emerald-600" />
                  <span>Sinkronisasi Data Kontak</span>
                </h4>
                <p className="text-slate-500 leading-relaxed text-[11px]">
                  Formulir di halaman kontak secara otomatis terhubung langsung dengan nomor WhatsApp{' '}
                  <strong className="text-slate-800">{settings?.contact?.whatsappNumber || '-'}</strong>{' '}
                  dan email resmi{' '}
                  <strong className="text-slate-800">{settings?.contact?.email || '-'}</strong>.
                </p>
                <div className="pt-2">
                  <button
                    onClick={() => copyPageUrl('/contact', 'Hubungi Kami')}
                    className="w-full py-2 bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 shadow-2xs"
                  >
                    <Copy className="w-3.5 h-3.5 text-slate-500" />
                    <span>Salin Link Halaman Kontak (/contact)</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 3: HALAMAN KUSTOM TAMBAHAN (CUSTOM PAGES) */}
      {/* ========================================================================= */}
      {activeTab === 'custom' && (
        <div className="space-y-6">
          <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h4 className="font-bold text-xs text-emerald-950">
                Buat Halaman Baru dengan Slug URL Sendiri
              </h4>
              <p className="text-[11px] text-emerald-800 mt-0.5">
                Tambahkan halaman Syarat & Ketentuan, Kebijakan Privasi, FAQ, Profil Tim, atau Layanan Khusus dengan URL permanen yang bisa disalin.
              </p>
            </div>
            {!isCreatingNew && !editingCustomPage && (
              <button
                onClick={() => {
                  setNewPageForm({
                    id: `cp-${Date.now()}`,
                    slug: '',
                    title: '',
                    subtitle: '',
                    content: '## Judul Halaman\n\nTuliskan informasi lengkap di sini...',
                    coverImage: '',
                    metaTitle: '',
                    metaDescription: '',
                    status: 'published',
                    updatedAt: new Date().toISOString(),
                  });
                  setIsCreatingNew(true);
                }}
                className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm shrink-0 transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>Buat Halaman Kustom Baru</span>
              </button>
            )}
          </div>

          {/* Form Editor for Creating or Editing a Custom Page */}
          {(isCreatingNew || editingCustomPage) && (
            <div className="bg-white p-6 rounded-2xl border-2 border-emerald-500/40 shadow-md space-y-5">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Edit3 className="w-4 h-4 text-emerald-600" />
                  <span>
                    {isCreatingNew
                      ? 'Buat Halaman Kustom Baru'
                      : `Edit Halaman: ${editingCustomPage?.title}`}
                  </span>
                </h3>
                <button
                  onClick={() => {
                    setIsCreatingNew(false);
                    setEditingCustomPage(null);
                  }}
                  className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-bold transition-colors"
                >
                  Batal
                </button>
              </div>

              {(() => {
                const currentForm = isCreatingNew ? newPageForm : editingCustomPage!;
                const updateForm = (updates: Partial<CustomStaticPage>) => {
                  if (isCreatingNew) {
                    setNewPageForm((prev) => ({ ...prev, ...updates }));
                  } else {
                    setEditingCustomPage((prev) => (prev ? { ...prev, ...updates } : null));
                  }
                };

                return (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1">
                          Judul Halaman *
                        </label>
                        <input
                          type="text"
                          required
                          value={currentForm.title}
                          onChange={(e) => {
                            const newTitle = e.target.value;
                            const autoSlug = newTitle
                              .toLowerCase()
                              .replace(/[^a-z0-9-]/g, '-')
                              .replace(/-+/g, '-')
                              .replace(/^-|-$/g, '');
                            updateForm({
                              title: newTitle,
                              slug: currentForm.slug ? currentForm.slug : autoSlug,
                            });
                          }}
                          placeholder="Contoh: Kebijakan Privasi & Ketentuan"
                          className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 font-medium text-slate-800"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold text-slate-700 block mb-1">
                          Slug URL Halaman (Permanen) *
                        </label>
                        <div className="flex items-center">
                          <span className="px-3 py-2.5 bg-slate-100 border border-r-0 border-slate-300 rounded-l-xl text-xs text-slate-500 font-mono">
                            /page/
                          </span>
                          <input
                            type="text"
                            required
                            value={currentForm.slug}
                            onChange={(e) =>
                              updateForm({
                                slug: e.target.value
                                  .toLowerCase()
                                  .replace(/[^a-z0-9-]/g, '-'),
                              })
                            }
                            placeholder="kebijakan-privasi"
                            className="w-full flex-1 px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-r-xl focus:ring-2 focus:ring-emerald-500 text-slate-800 font-mono font-bold"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">
                        Subjudul / Ringkasan Singkat (Opsional)
                      </label>
                      <input
                        type="text"
                        value={currentForm.subtitle || ''}
                        onChange={(e) => updateForm({ subtitle: e.target.value })}
                        placeholder="Pedoman penggunaan platform dan kerahasiaan data pengguna"
                        className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 text-slate-800"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">
                        Foto Sampul Halaman (Opsional)
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="text"
                          value={currentForm.coverImage || ''}
                          onChange={(e) => updateForm({ coverImage: e.target.value })}
                          placeholder="https://images.unsplash.com/..."
                          className="w-full flex-1 px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 text-slate-800"
                        />
                        <label className="cursor-pointer px-4 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold flex items-center gap-2 shrink-0 transition-colors">
                          <Upload className="w-3.5 h-3.5" />
                          <span>{uploadingImage ? 'Mengompres...' : 'Pilih Foto'}</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, 'custom')}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">
                        Konten Lengkap Halaman (Format Markdown) *
                      </label>
                      <textarea
                        rows={10}
                        required
                        value={currentForm.content}
                        onChange={(e) => updateForm({ content: e.target.value })}
                        className="w-full p-4 text-xs font-mono bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 text-slate-800 leading-relaxed"
                      />
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 text-xs font-bold text-slate-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={currentForm.status === 'published'}
                            onChange={(e) =>
                              updateForm({
                                status: e.target.checked ? 'published' : 'draft',
                              })
                            }
                            className="rounded text-emerald-600 focus:ring-emerald-500 w-4 h-4"
                          />
                          <span>Publikasikan Halaman Ini (Status: Aktif)</span>
                        </label>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setIsCreatingNew(false);
                            setEditingCustomPage(null);
                          }}
                          className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold"
                        >
                          Batal
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSaveCustomPage(currentForm)}
                          disabled={isSaving}
                          className="px-5 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold shadow-md flex items-center gap-2"
                        >
                          <Check className="w-4 h-4" />
                          <span>{isSaving ? 'Menyimpan...' : 'Simpan Halaman'}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* List of Custom Pages */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-sm text-slate-900">
                Daftar Halaman Kustom ({customPages.length})
              </h3>
            </div>

            {customPages.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <Layers className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <h4 className="font-bold text-slate-700 text-sm">Belum Ada Halaman Kustom</h4>
                <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                  Halaman "Tentang Kami" dan "Hubungi Kami" sudah aktif secara bawaan. Anda dapat membuat halaman baru seperti Kebijakan Privasi atau Syarat & Ketentuan di sini.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {customPages.map((page) => (
                  <div
                    key={page.id}
                    className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50/80 transition-colors"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold text-sm text-slate-900">{page.title}</h4>
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            page.status === 'published'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-slate-100 text-slate-500 border border-slate-200'
                          }`}
                        >
                          {page.status === 'published' ? 'Published' : 'Draft'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span className="font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-[11px]">
                          /page/{page.slug}
                        </span>
                        <span>• Diperbarui: {new Date(page.updatedAt).toLocaleDateString('id-ID')}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => copyPageUrl(`/page/${page.slug}`, page.title)}
                        className="px-3 py-1.5 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-2xs transition-colors"
                        title="Salin Tautan URL Halaman Ini"
                      >
                        <Copy className="w-3.5 h-3.5 text-slate-500" />
                        <span>Salin Link</span>
                      </button>

                      <button
                        onClick={() => navigate('custom-page', page.slug)}
                        className="px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-2xs transition-colors"
                        title="Lihat Halaman"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        <span>Buka</span>
                      </button>

                      <button
                        onClick={() => {
                          setEditingCustomPage(page);
                          setIsCreatingNew(false);
                        }}
                        className="p-1.5 bg-slate-100 hover:bg-emerald-50 text-slate-600 hover:text-emerald-700 rounded-lg transition-colors"
                        title="Edit Konten"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => setPageToDelete(page)}
                        className="p-1.5 bg-slate-100 hover:bg-red-50 text-slate-400 hover:text-red-600 rounded-lg transition-colors cursor-pointer"
                        title="Hapus Halaman"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={Boolean(pageToDelete)}
        title="Hapus Halaman Statis"
        message={`Apakah Anda yakin ingin menghapus halaman "${pageToDelete?.title || 'ini'}"?`}
        confirmLabel="Ya, Hapus Halaman"
        isDanger={true}
        onConfirm={executeDeleteCustomPage}
        onCancel={() => setPageToDelete(null)}
      />
    </div>
  );
}
